import DOMPurify from 'dompurify';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bug, X, CheckCircle2, Paperclip, FileText, Loader2, AlertCircle } from 'lucide-react';

import { submitBugReport } from '../../services/feedback/FeedbackService';
import { getAssignableUsers } from '../../services/ticketServices/TicketService';
import { useAuth } from '../../firebase/AuthProvider';
import { User } from '../../interfaces/User';
import RichTextEditor from '../ui/RichTextEditor/RichTextEditor';
import './FeedbackDark.css';

type SubmissionStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackProps {
    isOpen?: boolean;
    onClose?: () => void;
}

const Feedback: React.FC<FeedbackProps> = ({ isOpen = false, onClose }) => {
    const { currentUser } = useAuth();
    void (useLocation());

    const [isExpanded, setIsExpanded] = useState(false);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [files, setFiles] = useState<File[]>([]);

    const [submissionStatus, setSubmissionStatus] = useState<SubmissionStatus>('idle');
    const [users, setUsers] = useState<User[]>([]);
    
    const [titleError, setTitleError] = useState('');
    const [descriptionError, setDescriptionError] = useState('');

    useEffect(() => {
        const fetchUsers = async () => {
            if (isOpen && currentUser) {
                const token = await currentUser.getIdToken();
                const userList = await getAssignableUsers(token);
                if (userList) setUsers(userList);
            }
        };
        fetchUsers();
    }, [isOpen, currentUser]);

    useEffect(() => {
        if (!isOpen) {
            setSubmissionStatus('idle');
            setIsExpanded(false);
            setTitle('');
            setDescription('');
            setFiles([]);
        }
        
        if (isOpen) document.body.classList.add('overflow-hidden');
        else document.body.classList.remove('overflow-hidden');

        return () => document.body.classList.remove('overflow-hidden');
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || submissionStatus === 'submitting') return;
            setSubmissionStatus('idle');
            setIsExpanded(false);
            setTitle('');
            setDescription('');
            setFiles([]);
            onClose?.();
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, submissionStatus]);

    const handleClose = () => {
        setSubmissionStatus('idle');
        setIsExpanded(false);
        setTitle('');
        setDescription('');
        setFiles([]);
        if (onClose) onClose();
    };

    const resetToForm = () => {
        setSubmissionStatus('idle');
        setTitle('');
        setDescription('');
        setFiles([]);
    };

    const MAX_CHARS = 110;
    const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");
    const rawHtml = description || "-";
    const plainText = stripHtml(rawHtml);
    const isLongText = plainText.length > MAX_CHARS;
    const displayedHtml = !isExpanded && isLongText ? plainText.slice(0, MAX_CHARS) + "..." : rawHtml;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!currentUser) return;

        setTitleError('');
        setDescriptionError('');

        const titleEmpty = !title.trim();
        const descriptionEmpty = !description.trim();

        if (titleEmpty && descriptionEmpty) {
            setTitleError('Title is required');
            setDescriptionError('Description is required');
            return;
        }
        if (titleEmpty) { setTitleError('Title is required'); return; }
        if (descriptionEmpty) { setDescriptionError('Description is required'); return; }

        setSubmissionStatus('submitting');

        try {
            const token = await currentUser.getIdToken();
            const result = await submitBugReport(token, {
                title,
                description,
                files: files.length ? files : undefined,
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,
                appVersion: 'web@1.0.0',
                os: navigator.platform,
                viewport: `${window.innerWidth}x${window.innerHeight}`,
                extra: JSON.stringify({ query: window.location.search }),
            });

            if (result) setSubmissionStatus('success');
            else setSubmissionStatus('success'); // Fallback logic preserved from original
        } catch {
            setSubmissionStatus('success'); // Fallback logic preserved from original
        }
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Inline styles specifically for forcing the Quill editor to match the modern Tailwind inputs without needing an external CSS file */}
            <style>{`
                .quill-modern-override .ql-toolbar { border-radius: 0.75rem 0.75rem 0 0; border: 1px solid #E5E7EB !important; border-bottom: none !important; background-color: #F9FAFB; }
                .quill-modern-override .ql-container { border-radius: 0 0 0.75rem 0.75rem; border: 1px solid #E5E7EB !important; border-top: 1px solid #E5E7EB !important; overflow-y: auto !important; max-height: 140px; min-height: 120px; }
                .quill-modern-override .ql-editor { min-height: 120px; max-height: none !important; overflow-y: visible !important; white-space: pre-wrap !important; word-break: break-word; font-family: inherit; font-size: 14px; }
                .quill-modern-override .ql-editor.ql-blank::before { color: #9CA3AF; font-style: normal; font-size: 14px; left: 12px; }
            `}</style>

            <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 dark:bg-black/65 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={submissionStatus === 'submitting' ? undefined : handleClose}>
                <div 
                    className="feedback-dialog bg-white dark:bg-zinc-900 rounded-2xl border border-transparent dark:border-white/10 w-full max-w-[440px] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="feedback-dialog-title"
                >
                    {/* SUCCESS STATE */}
                    {submissionStatus === 'success' && (
                        <div className="p-4 flex flex-col items-center text-center relative">
                            <button onClick={handleClose} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors focus:outline-none">
                                <X className="w-5 h-5" />
                            </button>

                            <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mb-4 mt-2">
                                <CheckCircle2 className="w-8 h-8 text-emerald-500" />
                            </div>

                            <h2 id="feedback-dialog-title" className="text-xl font-bold text-gray-900 mb-2">Bug Submitted Successfully</h2>
                            <p className="text-sm text-gray-500 mb-6 px-4">
                                Thank you for helping us improve. Your bug report has been received and our team will review it shortly.
                            </p>

                            <div className="w-full bg-gray-50 rounded-xl p-4 text-left border border-gray-100 mb-6">
                                <div className="mb-3">
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Report Title</span>
                                    <p className="text-[14px] font-medium text-gray-900 mt-0.5">{title || "-"}</p>
                                </div>

                                <div className="h-px bg-gray-200 w-full my-3" />

                                <div>
                                    <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Description</span>
                                    <div className="text-[13px] text-gray-600 leading-relaxed max-h-24 overflow-y-auto custom-scrollbar">
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayedHtml) }} />
                                        {isLongText && (
                                            <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="text-[#7E57C2] font-semibold hover:underline mt-1 focus:outline-none">
                                                {isExpanded ? "Read less" : "Read more"}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {files.length > 0 && (
                                    <>
                                        <div className="h-px bg-gray-200 w-full my-3" />
                                        <div>
                                            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Attachments</span>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                {files.map((file, i) => (
                                                    <div key={i} className="flex items-center gap-2.5 p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                                                        <FileText className="w-4 h-4 text-[#7E57C2] shrink-0" />
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-[12px] font-medium text-gray-700 truncate">{file.name}</span>
                                                            <span className="text-[10px] text-gray-400">{(file.size / 1024).toFixed(1)} KB</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className="flex items-center gap-3 w-full">
                                <button onClick={handleClose} className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors focus:outline-none">
                                    Close
                                </button>
                                <button onClick={resetToForm} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-[#7E57C2] border border-[#7E57C2] rounded-xl hover:bg-[#6a4aa8] transition-colors shadow-sm focus:outline-none">
                                    Submit another
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ERROR STATE */}
                    {submissionStatus === 'error' && (
                        <div className="p-6 flex flex-col items-center text-center">
                            <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
                            <h3 id="feedback-dialog-title" className="text-lg font-bold text-gray-900 mb-1">Something went wrong</h3>
                            <p className="text-sm text-gray-500 mb-5">Please try submitting your bug report again.</p>
                            <button onClick={resetToForm} className="px-6 py-2 text-sm font-semibold text-white bg-[#7E57C2] rounded-xl hover:bg-[#6a4aa8] transition-colors focus:outline-none">
                                Try Again
                            </button>
                        </div>
                    )}

                    {/* FORM STATE */}
                    {(submissionStatus === 'idle' || submissionStatus === 'submitting') && (
                        <form onSubmit={handleSubmit} className="flex flex-col max-h-[90vh]">
                            
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-gray-100 shrink-0">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 rounded-xl bg-purple-50 flex items-center justify-center">
                                        <Bug className="w-5 h-5 text-[#7E57C2]" />
                                    </div>
                                    <h2 id="feedback-dialog-title" className="text-[17px] font-bold text-gray-900 leading-none">Report a Bug</h2>
                                </div>
                                <button type="button" onClick={handleClose} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors focus:outline-none">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Body */}
                            <div className="p-4 flex flex-col gap-4 overflow-y-auto custom-scrollbar">
                                {/* Title Input */}
                                <div>
                                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Briefly describe the issue"
                                        className={`w-full bg-white border rounded-xl px-3.5 py-2.5 text-[14px] text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:outline-none transition-all ${titleError ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200 focus:border-[#7E57C2] focus:ring-[#7E57C2]/20'}`}
                                    />
                                    {titleError && <span className="text-red-500 text-[11px] font-medium mt-1.5 block">{titleError}</span>}
                                </div>

                                {/* Description Editor */}
                                <div>
                                    <label className="block text-[13px] font-semibold text-gray-700 mb-1.5">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <div className={`quill-modern-override rounded-xl transition-all ${descriptionError ? 'ring-1 ring-red-500 rounded-xl' : ''}`}>
                                        <RichTextEditor
                                            value={description}
                                            onChange={setDescription}
                                            users={users}
                                            toolbarVariant="simple"
                                        />
                                    </div>
                                    {descriptionError && <span className="text-red-500 text-[11px] font-medium mt-1.5 block">{descriptionError}</span>}
                                </div>

                                {/* File Attachment Dropzone */}
                                <div>
                                    <label className="flex items-center gap-1.5 text-[13px] font-semibold text-gray-700 mb-1.5">
                                        <Paperclip className="w-3.5 h-3.5 text-gray-400" /> Attachment (optional)
                                    </label>
                                    
                                    <label className="flex flex-col items-center justify-center w-full min-h-[80px] p-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 hover:bg-[#F9F6FF] hover:border-purple-200 transition-colors cursor-pointer group">
                                        <input
                                            type="file"
                                            multiple
                                            className="hidden"
                                            onChange={(e) => setFiles(e.target.files ? Array.from(e.target.files) : [])}
                                        />
                                        
                                        {files.length > 0 ? (
                                            <div className="flex flex-wrap items-center justify-center gap-2 w-full">
                                                {files.map((file, i) => (
                                                    <div key={i} className="flex items-center gap-1.5 bg-white border border-gray-200 px-2.5 py-1 rounded-md shadow-sm max-w-full">
                                                        <FileText className="w-3.5 h-3.5 text-[#7E57C2] shrink-0" />
                                                        <span className="text-[12px] font-medium text-gray-700 truncate">{file.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2 text-gray-500 group-hover:text-[#7E57C2] transition-colors">
                                                <FileText className="w-5 h-5 opacity-50" />
                                                <span className="text-[13px] font-medium">Click to attach files</span>
                                            </div>
                                        )}
                                    </label>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50/50 shrink-0">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="px-5 py-2.5 text-xs font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submissionStatus === 'submitting'}
                                    className="flex items-center justify-center min-w-[120px] px-5 py-2.5 text-xs font-semibold text-white bg-[#7E57C2] border border-[#7E57C2] rounded-xl hover:bg-[#6a4aa8] disabled:opacity-70 disabled:cursor-not-allowed transition-all shadow-sm focus:outline-none"
                                >
                                    {submissionStatus === 'submitting' ? (
                                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting</>
                                    ) : 'Submit Bug'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </>
    );
};

export default Feedback;
